const delay = (time) => (next) => (url, opts) => {
  return new Promise((res) => setTimeout(() => res(next(url, opts)), time));
};
const defaultSkip$1 = (_, opts) => opts.skipDedupe || opts.method !== "GET";
const defaultKey$1 = (url, opts) => opts.method + "@" + url;
const defaultResolver = (response) => response.clone();
const dedupe = ({ skip = defaultSkip$1, key = defaultKey$1, resolver = defaultResolver } = {}) => {
  const inflight = /* @__PURE__ */ new Map();
  return (next) => (url, opts) => {
    if (skip(url, opts)) {
      return next(url, opts);
    }
    const _key = key(url, opts);
    if (!inflight.has(_key)) {
      inflight.set(_key, []);
    } else {
      return new Promise((resolve, reject) => {
        inflight.get(_key).push([resolve, reject]);
      });
    }
    try {
      return next(url, opts).then((response) => {
        inflight.get(_key).forEach(([resolve]) => resolve(resolver(response)));
        inflight.delete(_key);
        return response;
      }).catch((error) => {
        inflight.get(_key).forEach(([resolve, reject]) => reject(error));
        inflight.delete(_key);
        throw error;
      });
    } catch (error) {
      inflight.delete(_key);
      return Promise.reject(error);
    }
  };
};
const defaultSkip = (url, opts) => opts.skipCache || opts.method !== "GET";
const defaultKey = (url, opts) => opts.method + "@" + url;
const defaultClear = (url, opts) => false;
const defaultInvalidate = (url, opts) => null;
const defaultCondition = (response) => response.ok;
const throttlingCache = ({ throttle = 1e3, skip = defaultSkip, key = defaultKey, clear = defaultClear, invalidate = defaultInvalidate, condition = defaultCondition, flagResponseOnCacheHit = "__cached" } = {}) => {
  const cache = /* @__PURE__ */ new Map();
  const inflight = /* @__PURE__ */ new Map();
  const throttling = /* @__PURE__ */ new Set();
  const throttleRequest = (_key) => {
    if (throttle && !throttling.has(_key)) {
      throttling.add(_key);
      setTimeout(() => {
        throttling.delete(_key);
      }, throttle);
    }
  };
  const middleware = (next) => (url, opts) => {
    const _key = key(url, opts);
    let invalidatePatterns = invalidate(url, opts);
    if (invalidatePatterns) {
      if (!(invalidatePatterns instanceof Array)) {
        invalidatePatterns = [invalidatePatterns];
      }
      invalidatePatterns.forEach((pattern) => {
        if (typeof pattern === "string") {
          cache.delete(pattern);
        } else if (pattern instanceof RegExp) {
          cache.forEach((_, key2) => {
            if (pattern.test(key2)) {
              cache.delete(key2);
            }
          });
        }
      });
    }
    if (clear(url, opts)) {
      cache.clear();
    }
    if (skip(url, opts)) {
      return next(url, opts);
    }
    if (throttling.has(_key)) {
      if (cache.has(_key)) {
        const cachedClone = cache.get(_key).clone();
        if (flagResponseOnCacheHit) {
          Object.defineProperty(cachedClone, flagResponseOnCacheHit, {
            value: _key,
            enumerable: false
          });
        }
        return Promise.resolve(cachedClone);
      } else if (inflight.has(_key)) {
        return new Promise((resolve, reject) => {
          inflight.get(_key).push([resolve, reject]);
        });
      }
    }
    if (!inflight.has(_key))
      inflight.set(_key, []);
    throttleRequest(_key);
    return next(url, opts).then((response) => {
      if (condition(response.clone())) {
        cache.set(_key, response.clone());
      }
      inflight.get(_key).forEach(([resolve]) => resolve(response.clone()));
      inflight.delete(_key);
      return response;
    }).catch((error) => {
      inflight.get(_key).forEach(([resolve, reject]) => reject(error));
      inflight.delete(_key);
      throw error;
    });
  };
  middleware.cacheResponse = function(key2, response) {
    throttleRequest(key2);
    cache.set(key2, response);
  };
  middleware.cache = cache;
  middleware.inflight = inflight;
  middleware.throttling = throttling;
  return middleware;
};
const defaultDelayRamp = (delay2, nbOfAttempts) => delay2 * nbOfAttempts;
const defaultUntil = (response) => response && response.ok;
const retry = ({ delayTimer = 500, delayRamp = defaultDelayRamp, maxAttempts = 10, until = defaultUntil, onRetry = null, retryOnNetworkError = false, resolveWithLatestResponse = false, skip } = {}) => {
  return (next) => (url, opts) => {
    let numberOfAttemptsMade = 0;
    if (skip && skip(url, opts)) {
      return next(url, opts);
    }
    const checkStatus = (response, error) => {
      return Promise.resolve(until(response, error)).then((done) => {
        if (!done) {
          numberOfAttemptsMade++;
          if (!maxAttempts || numberOfAttemptsMade <= maxAttempts) {
            return new Promise((resolve) => {
              const delay2 = delayRamp(delayTimer, numberOfAttemptsMade);
              setTimeout(() => {
                if (typeof onRetry === "function") {
                  Promise.resolve(onRetry({
                    response,
                    error,
                    url,
                    options: opts
                  })).then((values = {}) => {
                    var _a, _b;
                    resolve(next((_a = values && values.url) !== null && _a !== void 0 ? _a : url, (_b = values && values.options) !== null && _b !== void 0 ? _b : opts));
                  });
                } else {
                  resolve(next(url, opts));
                }
              }, delay2);
            }).then(checkStatus).catch((error2) => {
              if (!retryOnNetworkError)
                throw error2;
              return checkStatus(null, error2);
            });
          } else {
            return !!response && resolveWithLatestResponse ? response : Promise.reject(error || new Error("Number of attempts exceeded."));
          }
        }
        return !!response && resolveWithLatestResponse ? response : error ? Promise.reject(error) : response;
      });
    };
    return next(url, opts).then(checkStatus).catch((error) => {
      if (!retryOnNetworkError)
        throw error;
      return checkStatus(null, error);
    });
  };
};
const CSRF_TOKEN_HEADER = "X-CSRF-Token";
function csrf() {
  let csrfToken = null;
  return (next) => async (url, wretchOptions) => {
    const response = await next(url, {
      ...wretchOptions,
      headers: {
        ...wretchOptions.headers,
        ...csrfToken !== null && {
          [CSRF_TOKEN_HEADER]: csrfToken
        }
      }
    });
    if (response.headers.has(CSRF_TOKEN_HEADER)) {
      csrfToken = response.headers.get(CSRF_TOKEN_HEADER);
    }
    return response;
  };
}
function defaultLogRequest(url, options) {
  console.log(`(REQ) [${options.method}] ${url}`, options);
}
async function defaultLogResponse(response) {
  const headers = response.headers;
  const contentType = headers.get("Content-Type");
  let content;
  if (contentType == null ? void 0 : contentType.includes("application/json")) {
    content = await response.json();
  } else if (contentType == null ? void 0 : contentType.includes("text/")) {
    content = await response.text();
  } else {
    content = response.body;
  }
  console.log(`(RES) [${response.status}] ${response.url}`, content);
}
function debug({
  logRequest,
  logResponse
} = {}) {
  const requestLogger = logRequest ?? defaultLogRequest;
  const responseLogger = logResponse ?? defaultLogResponse;
  return (next) => async (url, wretchOptions) => {
    await requestLogger(url, wretchOptions);
    const response = await next(url, wretchOptions);
    await responseLogger(response.clone());
    return response;
  };
}
export {
  csrf,
  debug,
  dedupe,
  delay,
  retry,
  throttlingCache
};
//# sourceMappingURL=middleware.js.map
